import { approxNumber, divRound, maxBig } from "./coin-int.ts";

/**
 * 旧收入独立模型：收入曲线与造价分开。0/缺省沿用原B；统一模型使用下方unifiedBAt。
 *
 * K 是万分比小参数（INT 列），仍按 INT32 校验；**算出来的 B 没有上限**（leo 2026-10-16）：
 * 整数算到底，锚 × K^step ÷ 10000^step 最后一步四舍五入。
 */
export function validIncomeGrowth(bps: number): boolean {
  return Number.isInteger(bps) && bps >= 0 && bps <= 2_147_483_647;
}
/** 首关收入 B：0 = 按造价取锚，否则正整数，没有上限 */
export function validIncomeAnchor(value: number | bigint): boolean {
  return typeof value === "bigint" ? value >= 0n : Number.isSafeInteger(value) && value >= 0;
}

/** 精确版。服务端结算一律用它 */
export function incomeGrowthBExact(legacyB: bigint, anchorB: bigint, step: number, bps?: number, anchorOverride: bigint = 0n): bigint {
  if (bps == null || bps === 0) return legacyB;
  if (!validIncomeGrowth(bps) || !Number.isSafeInteger(step) || step < 0 || !(anchorB > 0n)) throw new RangeError('收入膨胀参数无效');
  if (!validIncomeAnchor(anchorOverride)) throw new RangeError('首关收入B无效');
  if (bps === 10_000) return anchorOverride || anchorB;
  const k = BigInt(step);
  return maxBig(1n, divRound((anchorOverride || anchorB) * BigInt(bps) ** k, 10_000n ** k));
}

/** 管端预览用的 number 版（web 直接 import）：同一条算式，过 2^53 只是近似值 */
export function incomeGrowthB(legacyB: number, anchorB: number, step: number, bps?: number, anchorOverride = 0): number {
  if (bps == null || bps === 0) return legacyB;
  if (!Number.isFinite(anchorB) || !Number.isFinite(anchorOverride) || !Number.isInteger(anchorOverride) || anchorOverride < 0) throw new RangeError('收入膨胀参数无效');
  return approxNumber(incomeGrowthBExact(BigInt(Math.round(legacyB) || 0), anchorB > 0 ? BigInt(Math.round(anchorB)) : 0n, step, bps, BigInt(anchorOverride)));
}

/**
 * 精确版：从既定价格取收入锚 B = round(total ÷ (rolls × E × ρ))，至少 1。
 * E 用毫 B（eTargetMilliB）、ρ 用万分比（costRatioBps）——都是配置里存的整数，避免小数。
 */
export function incomeBFromPriceExact(total: bigint, rolls: bigint, eMilliB: number, rhoBps: number): bigint | null {
  if (!(total > 0n) || !(rolls > 0n) || !(eMilliB > 0) || !(rhoBps > 0)) return null;
  if (!Number.isSafeInteger(eMilliB) || !Number.isSafeInteger(rhoBps)) throw new RangeError('E 与 ρ 须为整数（毫B / 万分比）');
  return maxBig(1n, divRound(total * 10_000_000n, rolls * BigInt(eMilliB) * BigInt(rhoBps)));
}

/** 管端预览用的 number 版：E 与 rho 使用人读的小数单位，结果是近似值 */
export function incomeBFromPrice(total: number, rolls: number, e: number, rho: number): number | null {
  if (!(total > 0) || !(rolls > 0) || !(e > 0) || !(rho > 0)) return null;
  return Math.max(1, Math.round(total / (rolls * e * rho)));
}

/** 统一模型：同一B驱动收入和造价。K留空/0按1；起始值留空用首关配置B，不从价格反推。 */
export function unifiedBAt(anchorB: bigint, level: number, bps = 0, override = 0n): bigint {
  const base = override > 0n ? override : anchorB;
  return incomeGrowthBExact(base, base, level - 1, bps || 10_000, override);
}
